import React, {useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';
import {getBowserState} from '@epic-core/common';
import root from 'window-or-global';
import {Button, CmsButtonProps, Typography, VerticalSpacer} from '../../elements';
import {emitEvent, useEpicSid} from 'epic-ue-shared';
import {AccentColorOptions} from 'epic-ue-theme';

const Styled = styled.div<{accentColor?: AccentColorOptions}>`
    &:before {
        background: ${(p) => (p.accentColor ? p.theme.palette.accent[p.accentColor] : '#000')};
        content: '';
        width: 100%;
        height: 2px;
        display: block;
    }
`;

interface DownloadCardUE5Props {
    accentColor?: AccentColorOptions;
    variant: 'download' | 'open';
    title?: string;
    description?: string;
    link?: CmsButtonProps;
    industrySelected: string;
    launcherURL?: string;
}

export const DownloadCardUE5 = ({
    accentColor,
    title,
    variant = 'download',
    description,
    link,
    industrySelected,
    launcherURL
}: DownloadCardUE5Props): JSX.Element => {
    const {mobile, tablet} = getBowserState();
    // Check if iPad, with or without 'Request Desktop Site' enabled
    const iPad =
        /iPad/.test(root.navigator.platform) ||
        (root.navigator.platform === 'MacIntel' && root.navigator.maxTouchPoints > 1);

    const [downloadUrl, setDownloadUrl] = useState('');

    const [mac, setMac] = useState(false);

    const epicSid = useEpicSid();

    //because of cloudfront caching root._epicGamesNav.browser needs to be checked in a useEffect()
    useEffect(() => {
        if (root._epicGamesNav && root._epicGamesNav.browser) {
            setMac(root._epicGamesNav.browser.mac);
        }
    }, [setMac]);

    const buttonType = variant === 'download' ? 'primary' : 'secondary';

    useEffect(() => {
        if (mobile || tablet || iPad) {
            setDownloadUrl('');
        } else {
            //on all /solutions/* pages (besides games). If the user clicks on the download card OR the download CTA in the nav, they should get the enterprise version
            const key =
                !!industrySelected && industrySelected !== 'games'
                    ? 'enterprise'
                    : 'unrealEngine';

            let buttonHref;
            buttonHref = `https://launcher-public-service-prod06.ol.epicgames.com/launcher/api/installer/download/EpicGamesLauncherInstaller.msi?productName=${key}`;
            if (mac) {
                buttonHref = `https://launcher-public-service-prod06.ol.epicgames.com/launcher/api/installer/download/EpicGamesLauncher.dmg?productName=${key}`;
            }

            if (epicSid) {
                buttonHref += `&trackingId=${epicSid}`;
            }

            if (variant === 'open' && launcherURL) {
                buttonHref = launcherURL;
            }

            setDownloadUrl(buttonHref);
        }
    }, [mac, mobile, tablet, iPad, epicSid]);

    const onClick = useCallback(() => {
        emitEvent({
            eventAction: 'client.download',
            eventLabel: industrySelected || variant,
            eventValue: 'unrealEngine'
        });
    }, [industrySelected, variant, emitEvent]);

    return (
        <Styled accentColor={accentColor}>
            <VerticalSpacer variant="heading" />
            <Typography variant="h3" big>
                {title}
            </Typography>
            <VerticalSpacer variant="paragraph" />
            <Typography variant="p">{description}</Typography>
            <VerticalSpacer variant="heading" />
            {!(mobile || tablet ) &&
                <Button type={buttonType} download href={downloadUrl} onClick={onClick} target="_blank">
                    {link?.label}
                </Button>
            }
        </Styled>
    );
};
